import { useCallback, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { useDispatch } from "react-redux";



import {

  downloadClubEventSummary,

  fetchClubEmployees,

  fetchClubEventSummaryReadiness,

  forceCloseClubEventSummaryDevice,

} from "../api/clubApi";

import { asClubEmployeesList } from "../helpers/clubEmployeeList";

import {

  buildEventSummaryReadiness,

  normalizeEventSummaryReadiness,

} from "../helpers/eventSummaryReadiness";

import {

  buildEventSummaryFallbackFilename,

  triggerBlobDownload,

} from "../helpers/fileDownload";

import { showToast } from "../store/features/toast/toastSlice";



/**

 * Shared flow: readiness check → download or pending-devices modal.

 * @param {{

 *   eventId: string | null | undefined;

 *   eventTitle?: string;

 *   eventStartdatetime?: string | Date | null;

 *   format?: "xlsx" | "csv";

 *   enabled?: boolean;

 *   clubId?: string | null;

 *   clubEmployees?: unknown[];

 * }} options

 */

export default function useEventSummaryDownload({

  eventId,

  eventTitle = "",

  eventStartdatetime = null,

  format = "xlsx",

  enabled = true,

  clubId = null,

  clubEmployees = [],

}) {

  const { t } = useTranslation();

  const dispatch = useDispatch();



  const [downloading, setDownloading] = useState(false);

  const [checkingReadiness, setCheckingReadiness] = useState(false);

  const [showPendingModal, setShowPendingModal] = useState(false);

  const [readinessData, setReadinessData] = useState(null);

  const [forceClosingKey, setForceClosingKey] = useState(null);



  const employeesCacheRef = useRef(

    Array.isArray(clubEmployees) ? clubEmployees : []

  );



  const trimmedEventId = String(eventId ?? "").trim();

  const trimmedClubId = String(clubId ?? "").trim();

  const isActive = enabled && Boolean(trimmedEventId);



  const resolveEmployees = useCallback(async () => {

    if (Array.isArray(clubEmployees) && clubEmployees.length > 0) {

      employeesCacheRef.current = clubEmployees;

      return clubEmployees;

    }

    if (employeesCacheRef.current.length > 0) {

      return employeesCacheRef.current;

    }

    if (!trimmedClubId) return [];



    try {

      const res = await fetchClubEmployees(trimmedClubId);

      const raw = res?.data ?? res;

      let list = asClubEmployeesList(raw);

      if (

        list.length === 0 &&

        raw &&

        typeof raw === "object" &&

        raw.data != null

      ) {

        list = asClubEmployeesList(raw.data);

      }

      employeesCacheRef.current = list;

      return list;

    } catch {

      return employeesCacheRef.current;

    }

  }, [clubEmployees, trimmedClubId]);



  const finalizeReadiness = useCallback(

    async (raw) => {

      const employees = await resolveEmployees();

      return buildEventSummaryReadiness(raw, employees);

    },

    [resolveEmployees]

  );



  const showError = useCallback(

    (err) => {

      dispatch(

        showToast({

          message:

            typeof err === "string"

              ? err

              : err?.message || t("CLUB_SALES_EVENT_SUMMARY_DOWNLOAD_ERROR"),

          type: "error",

        })

      );

    },

    [dispatch, t]

  );



  const showSuccess = useCallback(

    (message) => {

      dispatch(

        showToast({

          message,

          type: "success",

        })

      );

    },

    [dispatch]

  );



  const openPendingModal = useCallback(

    async (data) => {

      const readiness = await finalizeReadiness(data);

      setReadinessData(readiness);

      setShowPendingModal(true);

    },

    [finalizeReadiness]

  );



  const closePendingModal = useCallback(() => {

    setShowPendingModal(false);

  }, []);



  const performDownload = useCallback(

    async (fmt) => {

      const formatValue = fmt === "csv" ? "csv" : "xlsx";

      const { blob, filename } = await downloadClubEventSummary(trimmedEventId, {

        format: formatValue,

      });

      const fallback = buildEventSummaryFallbackFilename(

        eventTitle || trimmedEventId,

        eventStartdatetime,

        formatValue

      );

      triggerBlobDownload(blob, filename || fallback);

      showSuccess(t("EVENT_SUMMARY_DOWNLOAD_SUCCESS"));

    },

    [

      trimmedEventId,

      eventTitle,

      eventStartdatetime,

      showSuccess,

      t,

    ]

  );



  const handleBlockedDownload = useCallback(

    async (err) => {

      if (err?.code === "EVENT_SUMMARY_BLOCKED" && err?.readiness) {

        await openPendingModal(err.readiness);

        return true;

      }

      return false;

    },

    [openPendingModal]

  );



  const handleDownload = useCallback(async () => {

    if (!isActive) return;

    setDownloading(true);

    try {

      const raw = await fetchClubEventSummaryReadiness(trimmedEventId);

      const readiness = await finalizeReadiness(raw);

      if (!readiness.canDownload) {

        setReadinessData(readiness);

        setShowPendingModal(true);

        return;

      }

      await performDownload(format);

    } catch (err) {

      if (!(await handleBlockedDownload(err))) {

        showError(err);

      }

    } finally {

      setDownloading(false);

    }

  }, [

    isActive,

    trimmedEventId,

    format,

    finalizeReadiness,

    performDownload,

    handleBlockedDownload,

    showError,

  ]);



  const handleCheckScannerStatus = useCallback(async () => {

    if (!isActive) return;

    setCheckingReadiness(true);

    try {

      const raw = await fetchClubEventSummaryReadiness(trimmedEventId);

      const readiness = await finalizeReadiness(raw);

      if (!readiness.canDownload) {

        setReadinessData(readiness);

        setShowPendingModal(true);

      } else {

        showSuccess(t("EVENT_SUMMARY_SCANNERS_READY"));

      }

    } catch (err) {

      showError(err);

    } finally {

      setCheckingReadiness(false);

    }

  }, [

    isActive,

    trimmedEventId,

    finalizeReadiness,

    showSuccess,

    showError,

    t,

  ]);



  const handleRetryReadiness = useCallback(async () => {

    if (!isActive) return;

    setCheckingReadiness(true);

    try {

      const raw = await fetchClubEventSummaryReadiness(trimmedEventId);

      const readiness = await finalizeReadiness(raw);

      setReadinessData(readiness);

      if (readiness.canDownload) {

        setShowPendingModal(false);

        setDownloading(true);

        try {

          await performDownload(format);

        } catch (err) {

          if (!(await handleBlockedDownload(err))) {

            showError(err);

          }

        } finally {

          setDownloading(false);

        }

      }

    } catch (err) {

      showError(err);

    } finally {

      setCheckingReadiness(false);

    }

  }, [

    isActive,

    trimmedEventId,

    format,

    finalizeReadiness,

    performDownload,

    handleBlockedDownload,

    showError,

  ]);



  const handleForceCloseDevice = useCallback(

    async (device, reason) => {

      if (!isActive || !device?.loginId || !device?.deviceId) return;

      const key = `${device.loginId}::${device.deviceId}`;

      setForceClosingKey(key);

      try {

        await forceCloseClubEventSummaryDevice(trimmedEventId, {

          loginId: device.loginId,

          deviceId: device.deviceId,

          reason,

          deactivateLogin: reason === "lost",

        });

        showSuccess(t("EVENT_SUMMARY_FORCE_CLOSE_SUCCESS"));

        const raw = await fetchClubEventSummaryReadiness(trimmedEventId);

        const readiness = await finalizeReadiness(raw);

        setReadinessData(readiness);

        if (readiness.canDownload) {

          setShowPendingModal(false);

          setDownloading(true);

          try {

            await performDownload(format);

          } catch (err) {

            if (!(await handleBlockedDownload(err))) {

              showError(err);

            }

          } finally {

            setDownloading(false);

          }

        }

      } catch (err) {

        showError(err);

      } finally {

        setForceClosingKey(null);

      }

    },

    [

      isActive,

      trimmedEventId,

      format,

      finalizeReadiness,

      performDownload,

      handleBlockedDownload,

      showSuccess,

      showError,

      t,

    ]

  );



  return {

    downloading,

    checkingReadiness,

    showPendingModal,

    readinessData,

    forceClosingKey,

    closePendingModal,

    handleDownload,

    handleCheckScannerStatus,

    handleRetryReadiness,

    handleForceCloseDevice,

  };

}

